// =============================================================================
// timetable-pin.js – "Segui questa corsa" pinned-trip helpers
// =============================================================================
// Persists a single pinned trip across renders / page reloads. State lives on
// `state.timetablePinnedTrip` (store.ts) and is mirrored to localStorage.
//
// Trip identity = { lineId, dayType, direction, tripId } (tripId alone is not
// unique across lines / day-types / directions).
// =============================================================================

import { getStopName } from "./line-config.js";
import { getScheduleKey, resolveDirection, escapeHtml } from "./utils.js";

const LS_KEY = "trasporti_timetable_pinned_trip";

/** Read the pinned trip from state (no fallback to localStorage — store.ts
 *  already bootstraps it from there). Returns null when no pin is set. */
export function getPinnedTrip(state) {
  return state?.timetablePinnedTrip || null;
}

/** Persist a new pinned trip. Pass null/undefined to clear. */
export function setPinnedTrip(state, trip) {
  if (!state) return;
  state.timetablePinnedTrip = trip || null;
  if (typeof localStorage === "undefined") return;
  try {
    if (trip) localStorage.setItem(LS_KEY, JSON.stringify(trip));
    else localStorage.removeItem(LS_KEY);
  } catch { /* ignore quota / private mode */ }
}

/** Toggle. Returns the new state (set or cleared). */
export function togglePinnedTrip(state, trip) {
  const current = getPinnedTrip(state);
  const isSame =
    current &&
    trip &&
    current.lineId === trip.lineId &&
    current.dayType === trip.dayType &&
    current.direction === trip.direction &&
    current.tripId === trip.tripId;
  if (isSame) {
    setPinnedTrip(state, null);
    return false;
  }
  setPinnedTrip(state, trip);
  return true;
}

/** Resolve the actual trip object from lineData for the pinned context, or
 *  null when the trip no longer exists (e.g. after a GTFS refresh). */
export function resolvePinnedTripObject(state, lineData) {
  const pin = getPinnedTrip(state);
  if (!pin) return null;
  const key = getScheduleKey(pin.lineId, pin.dayType, pin.direction);
  const trips = lineData?.[pin.lineId]?.[key] || [];
  return trips.find((t) => String(t.tripId) === String(pin.tripId)) || null;
}

/** True when the pinned trip's context matches the current view (line/day/
 *  direction). Used to decide whether to expand + scroll to its card or to
 *  show the out-of-context banner above the timetable. */
export function isPinnedTripInCurrentContext(state) {
  const pin = getPinnedTrip(state);
  if (!pin) return false;
  const lineId = state.timetableLine;
  const dayType = state.timetableDayType;
  const direction = resolveDirection(state.timetableDirection || "outbound", state.settings);
  return (
    pin.lineId === lineId &&
    pin.dayType === dayType &&
    pin.direction === direction
  );
}

/** Build the HTML for the pinned-trip banner above .line-select.
 *  Returns "" when no pin is set, or when the pin's line is not yet loaded
 *  (caller should call loadSingleLine first via the returned pin metadata). */
export function renderPinnedTripBanner(state, lineData, lineConfig, currentMin, crossDay) {
  const pin = getPinnedTrip(state);
  if (!pin) return "";
  const lineCfg = lineConfig?.[pin.lineId];
  if (!lineCfg) {
    // Unknown line — offer to clear, don't crash.
    return renderClearOnlyBanner("Linea del pin non riconosciuta");
  }

  // Pull the trip object if available. If pin's line isn't loaded yet, show
  // a loading-state banner with a clear button.
  const key = getScheduleKey(pin.lineId, pin.dayType, pin.direction);
  const trip = (lineData?.[pin.lineId]?.[key] || []).find(
    (t) => String(t.tripId) === String(pin.tripId)
  );

  const fromStop = trip ? findFirstStopOfTrip(trip) : null;
  const toStop = trip ? findLastStopOfTrip(trip) : null;
  const depMin = trip && fromStop ? trip.stops?.[fromStop] : null;
  const arrMin = trip && toStop ? trip.stops?.[toStop] : null;

  const inContext = isPinnedTripInCurrentContext(state);
  const lineLabel = lineConfig?.[pin.lineId]?.label || pin.lineId;
  const dayLabel = pin.dayType === "weekday" ? "Fer" : pin.dayType === "saturday" ? "Sab" : "Dom";
  const dirLabel = pin.direction === "outbound" ? "Andata" : "Ritorno";

  if (!trip) {
    // Pin exists but trip is missing — likely post-GTFS refresh. Show banner
    // with clear option.
    return `
      <div class="pinned-trip-banner out-of-sync" data-pinned-trip-banner style="--lc: var(--line-${escapeHtml(pin.lineId)}, var(--accent));">
        <span class="pin-icon">📌</span>
        <div class="pt-info">
          <span class="pt-line">${escapeHtml(pin.lineId)} · ${escapeHtml(lineLabel)}</span>
          <span class="pt-route">Corsa #${escapeHtml(String(pin.tripId))} non trovata — ${escapeHtml(dayLabel)} ${escapeHtml(dirLabel)}</span>
        </div>
        <button type="button" class="pt-unpin" data-pinned-trip-clear aria-label="Rimuovi pin">×</button>
      </div>
    `;
  }

  const depStr = depMin != null ? minsToHHMM(depMin) : "—";
  const arrStr = arrMin != null ? minsToHHMM(arrMin) : "—";
  const fromName = fromStop ? (getStopName(fromStop) || fromStop) : "—";
  const toName = toStop ? (getStopName(toStop) || toStop) : "—";

  let countdownHtml = "";
  if (!crossDay && depMin != null) {
    const diff = depMin - currentMin;
    let cls = "late";
    let text = `${diff} min`;
    if (diff < 0) { cls = "partito"; text = "partito"; }
    else if (diff === 0) { cls = "ora"; text = "ora"; }
    else if (diff <= 5) { cls = "soon"; text = `${diff} min`; }
    else if (diff <= 15) { cls = "mid"; text = `${diff} min`; }
    countdownHtml = `<span class="countdown ${cls}">${text}</span>`;
  }

  const contextHint = inContext
    ? ""
    : `<span class="pt-context-hint">⚠ non in questa vista (${escapeHtml(dayLabel)} ${escapeHtml(dirLabel)})</span>`;

  return `
    <div class="pinned-trip-banner${inContext ? "" : " out-of-context"}" data-pinned-trip-banner style="--lc: var(--line-${escapeHtml(pin.lineId)}, var(--accent));">
      <span class="pin-icon">📌</span>
      <div class="pt-info">
        <span class="pt-line">${escapeHtml(pin.lineId)} <small>${escapeHtml(lineLabel)}</small></span>
        <span class="pt-route">${escapeHtml(fromName)} → ${escapeHtml(toName)} · ${depStr}–${arrStr}</span>
        ${contextHint}
      </div>
      ${countdownHtml}
      <button type="button" class="pt-unpin" data-pinned-trip-clear aria-label="Rimuovi pin" title="Rimuovi pin">×</button>
    </div>
  `;
}

function renderClearOnlyBanner(message) {
  return `
    <div class="pinned-trip-banner out-of-sync" data-pinned-trip-banner>
      <span class="pin-icon">📌</span>
      <div class="pt-info">
        <span class="pt-route">${escapeHtml(message)}</span>
      </div>
      <button type="button" class="pt-unpin" data-pinned-trip-clear aria-label="Rimuovi pin">×</button>
    </div>
  `;
}

function findFirstStopOfTrip(trip) {
  if (!trip?.stops) return null;
  const entries = Object.entries(trip.stops)
    .filter(([, t]) => t != null)
    .sort((a, b) => a[1] - b[1]);
  return entries.length ? entries[0][0] : null;
}

function findLastStopOfTrip(trip) {
  if (!trip?.stops) return null;
  const entries = Object.entries(trip.stops)
    .filter(([, t]) => t != null)
    .sort((a, b) => a[1] - b[1]);
  return entries.length ? entries[entries.length - 1][0] : null;
}

// Local HH:MM formatter (avoids importing the whole utils bundle when this
// module is re-used in tests).
function minsToHHMM(min) {
  if (min == null || Number.isNaN(min)) return "—";
  const m = ((min % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}
